import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Save,
  Close,
  Download,
  Share,
  Assignment,
  Person,
  Edit,
  ViewList,
  ViewModule,
  FormatBold,
  FormatItalic,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  InsertLink,
  Image,
  TableChart,
  Undo,
  Redo,
} from '@mui/icons-material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { getAuthToken } from '../../lib/authToken';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  category: string;
  tags: string[] | string;
  isConfidential: boolean;
  isPublic: boolean;
  isTemplate: boolean;
  version: number;
  caseId: string | null;
  clientId: string | null;
  assignedTo: string[];
  uploadedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface DocumentEditorProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  onSave?: () => void;
  /** Optional case context (Case Folder); unused by API but kept for callers */
  caseId?: string;
}

interface DocumentFormData {
  title: string;
  description: string;
  documentType: string;
  category: string;
  tags: string[];
  isConfidential: boolean;
  isPublic: boolean;
  caseId: string | null;
  clientId: string | null;
  assignedTo: string[];
  content: string;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
  open,
  onClose,
  document,
  onSave,
}) => {
  const quillRef = useRef<ReactQuill>(null);

  // Quill modules configuration - simplified to avoid table module errors
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    },
  }), []);

  const formats = useMemo(() => [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link',
    'color', 'background',
    'align', 'direction'
  ], []);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<DocumentFormData>({
    title: '',
    description: '',
    documentType: '',
    category: '',
    tags: [],
    isConfidential: false,
    isPublic: false,
    caseId: null,
    clientId: null,
    assignedTo: [],
    content: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [editable, setEditable] = useState(true);
  const [editorKey, setEditorKey] = useState(0);

  // Fetch tenant users for assignment / sharing (not Team Management)
  const { data: usersData, error: usersError } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    queryFn: fetchAssignableUsers,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const users = asAssignableUsers(usersData);

  // Load editable content via authenticated API (storage-safe; works with S3)
  useEffect(() => {
    if (!document || !open) return;

    let cancelled = false;
    const loadContent = async () => {
      setContentLoading(true);
      setContentError(null);
      setFormData({
        title: document.title || '',
        description: document.description || '',
        documentType: document.documentType || '',
        category: document.category || '',
        tags: Array.isArray(document.tags) ? document.tags : [],
        isConfidential: document.isConfidential || false,
        isPublic: document.isPublic || false,
        caseId: document.caseId,
        clientId: document.clientId,
        assignedTo: document.assignedTo || [],
        content: '',
      });

      try {
        const token = getAuthToken();
        const response = await fetch(`/api/documents/${document.id}/content`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`Failed to load content (${response.status})`);
        }
        const result = await response.json();
        const payload = result.data || {};
        if (cancelled) return;

        setEditable(payload.editable !== false);
        if (payload.editable === false) {
          setContentError(payload.reason || 'This document cannot be edited in the browser.');
        }
        setFormData((prev) => ({
          ...prev,
          content: payload.content || '',
        }));
        setEditorKey((k) => k + 1);
      } catch (err: any) {
        if (!cancelled) {
          setContentError(err?.message || 'Failed to load document content');
          setEditable(false);
        }
      } finally {
        if (!cancelled) setContentLoading(false);
      }
    };

    loadContent();
    return () => {
      cancelled = true;
    };
  }, [document?.id, open]);

  // Save: metadata + versioned content body
  const saveDocumentMutation = useMutation({
    mutationFn: async (data: DocumentFormData) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const metaResponse = await fetch(`/api/documents/${document?.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: data.title,
          description: data.description,
          documentType: data.documentType,
          category: data.category,
          tags: data.tags,
          isConfidential: data.isConfidential,
          isPublic: data.isPublic,
          caseId: data.caseId || undefined,
          clientId: data.clientId || undefined,
        }),
      });

      if (!metaResponse.ok) {
        const errorData = await metaResponse.text();
        throw new Error(`Failed to save properties: ${metaResponse.status} ${errorData}`);
      }

      if (editable) {
        const contentResponse = await fetch(`/api/documents/${document?.id}/content`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: data.content || '',
            changeDescription: 'Edited in ArgisLegal document editor',
          }),
        });
        if (!contentResponse.ok) {
          const errorData = await contentResponse.text();
          throw new Error(`Failed to save content: ${contentResponse.status} ${errorData}`);
        }
      }

      return metaResponse.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['case-documents'] });
      toast.success('Document saved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save document: ${error.message}`);
    },
  });

  const handleSave = async () => {
    if (!document?.id) {
      toast.error('No document selected for editing');
      return;
    }

    setIsSubmitting(true);
    try {
      await saveDocumentMutation.mutateAsync(formData);
      if (typeof onSave === 'function') {
        onSave();
      }
    } catch {
      // toast handled in mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof DocumentFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTagChange = (event: any, newValue: string[]) => {
    setFormData(prev => ({
      ...prev,
      tags: newValue,
    }));
  };

  const handleAssignedToChange = (event: any, newValue: string[]) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: newValue,
    }));
  };

  const formatContent = (content: string) => {
    if (!content) return <Typography color="text.secondary">No content available</Typography>;
    
    // Display HTML content safely
    return (
      <Box 
        sx={{ 
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginBottom: 1,
            marginTop: 2,
          },
          '& p': {
            marginBottom: 1,
          },
          '& ul, & ol': {
            marginBottom: 1,
            paddingLeft: 2,
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: 1,
          },
          '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            padding: 1,
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
          },
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {document ? `Edit Document: ${document.title}` : 'Create New Document'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={viewMode === 'edit' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<Edit />}
              onClick={() => setViewMode('edit')}
            >
              Edit
            </Button>
            <Button
              variant={viewMode === 'preview' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<ViewModule />}
              onClick={() => setViewMode('preview')}
            >
              Preview
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {viewMode === 'edit' ? (
          <Grid container spacing={3} sx={{ height: '100%' }}>
            {/* Document Properties */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Document Properties
                </Typography>
                
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={formData.documentType}
                    onChange={(e) => handleInputChange('documentType', e.target.value)}
                    label="Document Type"
                  >
                    <MenuItem value="pleading">Pleading</MenuItem>
                    <MenuItem value="discovery">Discovery</MenuItem>
                    <MenuItem value="correspondence">Correspondence</MenuItem>
                    <MenuItem value="evidence">Evidence</MenuItem>
                    <MenuItem value="court_filing">Court Filing</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="agreement">Agreement</MenuItem>
                    <MenuItem value="invoice">Invoice</MenuItem>
                    <MenuItem value="receipt">Receipt</MenuItem>
                    <MenuItem value="id_document">ID Document</MenuItem>
                    <MenuItem value="medical_record">Medical Record</MenuItem>
                    <MenuItem value="police_report">Police Report</MenuItem>
                    <MenuItem value="expert_report">Expert Report</MenuItem>
                    <MenuItem value="witness_statement">Witness Statement</MenuItem>
                    <MenuItem value="photograph">Photograph</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                    <MenuItem value="audio">Audio</MenuItem>
                    <MenuItem value="email_attachment">Email Attachment</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.tags}
                  onChange={handleTagChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                  }
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant={formData.isConfidential ? 'contained' : 'outlined'}
                    color="error"
                    size="small"
                    onClick={() => handleInputChange('isConfidential', !formData.isConfidential)}
                  >
                    Confidential
                  </Button>
                  <Button
                    variant={formData.isPublic ? 'contained' : 'outlined'}
                    color="success"
                    size="small"
                    onClick={() => handleInputChange('isPublic', !formData.isPublic)}
                  >
                    Public
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Assignment
                </Typography>

                <Autocomplete
                  multiple
                  options={users.map((user: any) => user.email || `${user.firstName} ${user.lastName}` || user.id)}
                  value={formData.assignedTo}
                  onChange={(event, newValue) => handleAssignedToChange(event, newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign To"
                      placeholder={usersError ? "Error loading users" : users.length === 0 ? "No users available" : "Select users..."}
                      error={!!usersError}
                      helperText={usersError ? "Failed to load users" : ""}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        icon={<Person />}
                      />
                    ))
                  }
                />
              </Box>
            </Grid>

            {/* Document Content Editor */}
            <Grid item xs={12} md={8}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Document Content
                </Typography>

                {document?.isTemplate && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Library template — placeholders like {'{{Client_FirstName}}'} stay as-is.
                    Client values are filled only when this template is imported into a case.
                  </Alert>
                )}
                {!document?.isTemplate && document?.caseId && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Case document — edit freely. Placeholders were populated when the template
                    was imported into this matter.
                  </Alert>
                )}
                {contentError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {contentError}
                  </Alert>
                )}
                {contentLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!contentLoading && editable && (
                  <Box sx={{
                    flexGrow: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    '& .ql-toolbar': {
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                    },
                    '& .ql-container': {
                      border: 'none',
                      fontFamily: theme.typography.body1.fontFamily,
                      fontSize: theme.typography.body1.fontSize,
                    },
                    '& .ql-editor': {
                      minHeight: '350px',
                      padding: theme.spacing(2),
                    },
                  }}>
                    {open && document && (
                      <ReactQuill
                        key={`${document.id}-${editorKey}`}
                        ref={quillRef}
                        theme="snow"
                        value={formData.content || ''}
                        onChange={(content) => handleInputChange('content', content)}
                        modules={modules}
                        formats={formats}
                        placeholder="Edit document content…"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          /* Preview Mode */
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Typography variant="h4" gutterBottom>
              {formData.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {formData.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Chip label={formData.documentType} color="primary" size="small" />
              {formData.isConfidential && (
                <Chip label="Confidential" color="error" size="small" />
              )}
              {formData.isPublic && (
                <Chip label="Public" color="success" size="small" />
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {formatContent(formData.content)}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentEditor; 